import React from "react";

// Read-only metadata returned by makerprogram.ConfirmedLaunchPolicy.
export interface MakerLaunchPolicy {
  version: string;
  maker_pool_fraction: string;
  options_fraction: string;
  perps_fraction: string;
  initial_epoch_count: number;
  post_epoch_action: string;
  allocations: {
    id: string;
    underlying: string;
    product: string;
    maker_pool_fraction: string;
    expiry_budgets_equal: boolean;
  }[];
  perps_depth_cap_usd: string;
  future_perps_depth_cap_usd: string;
  options_depth_cap_underlying_notional_usd: string;
  cap_order: string;
  sample_interval_seconds: number;
  hourly_presence_fraction: string;
  hourly_eligibility_rule: string;
  account_allocation_cap_fraction: string;
  volatile_presence_fraction: string;
  minimum_volatile_checks: number;
  volatility_score_multiplier: number;
  volatility_presence_weighted: boolean;
  quiet_week_volatile_requirement: string;
  account_grouping_policy: string;
  volume_reward_exclusion_scope: string;
  rebates_can_combine_with_quote_rewards: boolean;
  customer_points_preserve_maker_rebate: boolean;
  option_greek_multiplier_status: string;
  option_tightness_weight_status: string;
  options_rewards_distributable: boolean;
  option_delta_weight: string;
  option_vega_multiplier_enabled: boolean;
  option_spread_weight: string;
  option_dte_convention: string;
  option_initial_minimum_hours: number;
  option_roll_before_settlement_hours: number;
  option_expiry_count: number;
  option_eligible_strikes_per_expiry: number;
  option_strike_refresh_seconds: number;
  volatility_lookback_seconds: number;
  volatility_move_fraction: string;
  benchmark_maximum_age_seconds: number;
  option_reward_scoring_ready: boolean;
  reward_settlement_ready: boolean;
  operational_blockers: { code: string; description: string }[];
  pending_decisions: { code: string; description: string }[];
}

export function fractionPercent(value: string) {
  const parts = /^(\d+)(?:\/(\d+))?$/.exec(value);
  if (!parts || (parts[2] && Number(parts[2]) === 0)) return "Unavailable";
  const percent = (Number(parts[1]) / Number(parts[2] || "1")) * 100;
  return Number.isFinite(percent)
    ? `${percent.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`
    : "Unavailable";
}

export function LaunchPolicy({ policy }: { policy?: MakerLaunchPolicy }) {
  return (
    <section aria-label="Confirmed launch policy">
      <h2>Confirmed launch policy</h2>
      {!policy ||
      typeof policy.option_reward_scoring_ready !== "boolean" ||
      !Array.isArray(policy.operational_blockers) ? (
        <p role="status">
          Launch policy is unavailable. Reward previews remain disabled.
        </p>
      ) : (
        <>
          <p>
            {fractionPercent(policy.maker_pool_fraction)} of weekly AEVO goes to
            makers: {fractionPercent(policy.options_fraction)} options /{" "}
            {fractionPercent(policy.perps_fraction)} perps. Fixed for the first{" "}
            {policy.initial_epoch_count} weekly epochs, then reviewed. There is
            no automatic increase to 30%.
          </p>
          <div style={{ overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Allocation</th>
                  <th>Maker pool share</th>
                  <th>Expiry split</th>
                </tr>
              </thead>
              <tbody>
                {policy.allocations.map((allocation) => (
                  <tr key={allocation.id}>
                    <td>{allocation.id}</td>
                    <td>{fractionPercent(allocation.maker_pool_fraction)}</td>
                    <td>
                      {allocation.expiry_budgets_equal
                        ? "Equal across two expiries"
                        : "Not applicable"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            Fixed {policy.sample_interval_seconds}-second samples;{" "}
            {fractionPercent(policy.hourly_presence_fraction)} presence in every
            agreed hour. Weekly averages do not establish hourly eligibility.
          </p>
          <p>
            {policy.hourly_eligibility_rule === "qualified_hours_only"
              ? "Only qualified hours contribute to the weekly score. A failed hour excludes only that hour's score; other qualified hours still count."
              : "Hourly score aggregation is unavailable from the server."}
          </p>
          <h3>Separate weekly volatility requirement</h3>
          <p>
            {fractionPercent(policy.volatile_presence_fraction)} of valid
            volatile checks across the week. This requirement applies only at{" "}
            {policy.minimum_volatile_checks}
            or more valid volatile checks.
          </p>
          {policy.quiet_week_volatile_requirement ===
            "waived_below_minimum" && (
            <p>
              Quiet weeks and weeks with fewer than{" "}
              {policy.minimum_volatile_checks} valid volatile checks waive this
              requirement.
            </p>
          )}
          <p>
            Volatility multiplier: {policy.volatility_score_multiplier}× on
            quote scores.{" "}
            {policy.volatility_presence_weighted === false &&
              "Presence is unweighted; each valid check counts once."}
          </p>
          <p>
            A volatile check requires at least{" "}
            {fractionPercent(policy.volatility_move_fraction)} absolute index
            movement over {policy.volatility_lookback_seconds / 60} minutes.
            Benchmark maximum age: {policy.benchmark_maximum_age_seconds}s.
          </p>
          <h3>Family participation and incentives</h3>
          {policy.account_grouping_policy === "parent_family_v1" && (
            <>
              <p>The maker parent and all subaccounts form one participant.</p>
              <p>
                The {fractionPercent(policy.account_allocation_cap_fraction)}{" "}
                cap applies per allocation across the whole family.
              </p>
              {policy.volume_reward_exclusion_scope ===
                "entire_family_all_markets_while_participating" && (
                <p>
                  All family accounts are excluded from volume rewards across
                  all markets while participating.
                </p>
              )}
            </>
          )}
          {policy.rebates_can_combine_with_quote_rewards && (
            <p>Maker rebates can combine with quote rewards.</p>
          )}
          {policy.customer_points_preserve_maker_rebate && (
            <p>
              A customer&apos;s full fee waiver using Points preserves the
              maker&apos;s rebate.
            </p>
          )}
          <p>
            Cap raw depth before price weighting: BTC/ETH perps $
            {policy.perps_depth_cap_usd} per maker per side; options $
            {policy.options_depth_cap_underlying_notional_usd} underlying-index
            notional per contract per side; future other perps $
            {policy.future_perps_depth_cap_usd} per side.
          </p>
          {!policy.reward_settlement_ready && (
            <p role="status">
              {policy.option_reward_scoring_ready
                ? "Options scoring is configured. "
                : "Options scoring is unavailable. "}
              Reward distribution remains blocked until authoritative weekly
              settlement and operational checks are verified. Claims remain
              deferred.
            </p>
          )}
          <details>
            <summary>Confirmed quote rules</summary>
            <p>
              Quotes qualify only after one full second resting in the live
              book. Price changes, size increases and cancel-and-replace reset
              only that order&apos;s age. Partial fills and size reductions
              preserve the remaining live order&apos;s age.
            </p>
            <p>
              New options expiries need at least{" "}
              {policy.option_initial_minimum_hours} hours remaining and are
              retained until {policy.option_roll_before_settlement_hours} hours
              before settlement. The next {policy.option_expiry_count} distinct
              eligible expiries share each underlying&apos;s budget equally.
              Publish {policy.option_eligible_strikes_per_expiry} eligible ATM
              strikes per expiry every{" "}
              {policy.option_strike_refresh_seconds / 3600} hour. Count only the
              best qualifying bid and ask; maximum spread is 5% of fair value or
              one tick, whichever is larger. Missing or stale benchmarks skip
              the measurement for everyone.
            </p>
            <p>
              Cap raw underlying-index notional before applying{" "}
              <code>{policy.option_delta_weight}</code>, following the existing
              weekly options delta weighting.{" "}
              {policy.option_vega_multiplier_enabled === false &&
                "No vega multiplier is applied."}
            </p>
            <p>
              Spread weight: <code>{policy.option_spread_weight}</code>.
            </p>
          </details>
          {!!policy.pending_decisions.length && (
            <details open>
              <summary>Decisions still pending</summary>
              <ul>
                {policy.pending_decisions.map((decision) => (
                  <li key={decision.code}>{decision.description}</li>
                ))}
              </ul>
            </details>
          )}
          {!!policy.operational_blockers.length && (
            <details open>
              <summary>Operational checks before settlement</summary>
              <ul>
                {policy.operational_blockers.map((blocker) => (
                  <li key={blocker.code}>{blocker.description}</li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </section>
  );
}
